/**
 * hrV1StepDerivation.ts
 *
 * Derives the 7-step workflow status from cycle + submission + appraisal
 * snapshot data. Used by WorkflowStepper to render an at-a-glance view
 * of where a cycle is.
 */

import type { EPRSubmission } from '../types/eprFramework';
import type { AppraisalForm } from '../types/appraisal';
import type { CalibrationSession } from '../types/calibration';
import type { AppealRecord } from '../types/appeal';
import type { WorkflowStep } from '../components/epr/WorkflowStepper';

export interface DeriveStepsInput {
  currentStep?: number | null;
  submissions: EPRSubmission[];
  appraisals: AppraisalForm[];
  session: CalibrationSession | null;
  openAppeals: AppealRecord[];
  outcomeCount?: number;
}

const STEP_LABELS = [
  'Submit EPR',
  '360 Review',
  'Consolidation',
  'Calibration',
  'Sign-off',
  'Appeals',
  'Outcome',
];

const STEP_ICONS = [
  'edit_note',
  'groups',
  'merge',
  'balance',
  'task_alt',
  'gavel',
  'workspace_premium',
];

export function deriveSteps(input: DeriveStepsInput): WorkflowStep[] {
  const {
    currentStep,
    submissions,
    appraisals,
    session,
    openAppeals,
    outcomeCount = 0,
  } = input;

  // Per-step done predicates
  const submissionsTotal = submissions.length;
  const managerSubmitted = submissions.filter(
    s => s.status === 'manager_submitted' || s.status === 'locked',
  ).length;

  const step1Done = submissionsTotal > 0 && managerSubmitted === submissionsTotal;

  // Step 2 is hard to measure without panel size; use "any 360 submitted"
  // as a "started" hint and step.currentStep >= 3 as "done"
  const step2Done = (currentStep ?? 0) >= 3;

  const step3Done = appraisals.length > 0;
  const step4Done = session?.status === 'locked';

  const appraisalsCount = appraisals.length;
  const signedCount = appraisals.filter(
    a => a.status === 'manager_signed' || a.status === 'closed',
  ).length;
  const step5Done = appraisalsCount > 0 && signedCount === appraisalsCount;

  const hasAppeals = openAppeals.length > 0;
  // Step 6 status: optional. Done when no open appeals AND step5 done.
  const step6Done = step5Done && !hasAppeals && (currentStep ?? 0) >= 7;
  const step6Skipped = step5Done && !hasAppeals && (currentStep ?? 0) >= 7 && appraisals.every(a => !a.appealId);

  const step7Done = appraisalsCount > 0 && outcomeCount === appraisalsCount;

  const dones = [step1Done, step2Done, step3Done, step4Done, step5Done, step6Done, step7Done];

  // Active step = first non-done
  const activeIdx = dones.findIndex(d => !d);
  const activeStep = activeIdx === -1 ? 7 : activeIdx + 1;

  const out: WorkflowStep[] = [];
  for (let i = 0; i < 7; i++) {
    const num = i + 1;
    let status: WorkflowStep['status'];
    if (dones[i]) {
      status = 'done';
    } else if (num === activeStep) {
      status = num === 6 && step6Skipped ? 'skipped' : 'active';
    } else if (num === 6 && step6Skipped) {
      status = 'skipped';
    } else if (num === 6 && !hasAppeals && (currentStep ?? 0) < 5) {
      status = 'optional';
    } else if (num === 6) {
      status = hasAppeals ? 'active' : 'optional';
    } else {
      status = 'pending';
    }

    let detail: string | undefined;
    switch (num) {
      case 1: detail = submissionsTotal > 0 ? `${managerSubmitted}/${submissionsTotal} done` : undefined; break;
      case 3: detail = appraisalsCount > 0 ? `${appraisalsCount} draft` : undefined; break;
      case 4: detail = session ? session.status : undefined; break;
      case 5: detail = appraisalsCount > 0 ? `${signedCount}/${appraisalsCount} signed` : undefined; break;
      case 6: detail = hasAppeals ? `${openAppeals.length} open` : undefined; break;
      case 7: detail = appraisalsCount > 0 ? `${outcomeCount}/${appraisalsCount}` : undefined; break;
    }

    out.push({
      num,
      label: STEP_LABELS[i],
      icon:  STEP_ICONS[i],
      status,
      detail,
    });
  }
  return out;
}
